// The "why can't I proceed?" answer for a gated action. A disabled button
// cannot be asked: it greys out, drops out of the tab order, and the Author is
// left guessing which of the things on screen is still missing. The gate keeps
// the muted look but stays a focusable button, carries the reason as its
// tooltip and accessible description exactly like NavSteps' locked steps, and
// answers a click by telling the screen to point at what it wants.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, type ButtonProps } from "./Button";

export interface Gate {
  /** True when the action cannot run yet. The button stays clickable to say why. */
  readonly blocked: boolean;
  /** The sentence that says what is missing, shown as the tooltip and read out. */
  readonly reason: ReactNode;
}

/**
 * A Button that refuses to proceed out loud. `onBlocked` runs when the Author
 * clicks while blocked: the screen highlights the offending fields and moves
 * focus to the first one, so the reason is not only in the tooltip.
 *
 * `disabled` stays for states that are nobody's turn: work already running, a
 * request in flight. `gate.blocked` is for the Author's own to-do, which a
 * greyed-out button cannot explain.
 */
export function GateButton({
  gate,
  onBlocked,
  disabled = false,
  children,
  ...rest
}: ButtonProps & {
  readonly gate: Gate;
  /** Called on a click while `gate.blocked`; the muted look stays until the reason is gone. */
  readonly onBlocked: () => void;
}) {
  const { t } = useTranslation("components");
  if (!gate.blocked || disabled)
    return (
      <Button {...rest} disabled={disabled}>
        {children}
      </Button>
    );
  return (
    <Button
      {...rest}
      aria-disabled="true"
      title={typeof gate.reason === "string" ? gate.reason : t("gate.blocked")}
      data-gated="true"
      className="cursor-not-allowed opacity-60"
      onClick={(event) => {
        event.preventDefault();
        onBlocked();
      }}
    >
      {children}
    </Button>
  );
}
